import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StyleSheet, View, Animated, Easing } from 'react-native';
import { Color, CommonProperties, defaultFontSize } from '../../styles';
import Modal from '../Modal';
import Icon from '../Icon';
import { scale } from '../../utils/scale.utils';

const Interstitial = ({ options, icon }) => {
  const animatedValue = useRef(new Animated.Value(0)).current;
  const animatedOpacity = useRef(new Animated.Value(0)).current;
  const animatedMarginLeft = useRef(new Animated.Value(0)).current;
  const animatedMarginRight = useRef(new Animated.Value(400)).current;
  const animatedOpacity2 = useRef(new Animated.Value(0)).current;
  const [title, setTitle] = useState(-1);

  const opacity = animatedValue.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 1, 0],
  });

  useLayoutEffect(() => {
    setTimeout(() => {
      animate();
      animateOpacity();
    }, 500);
  }, [animate, animateOpacity]);

  useEffect(() => {
    animate();
  }, [title, animate]);

  // TODO: Improve the animation, instead of use left, use transforX, because left doesnt have native support
  const animate = useCallback(() => {
    animatedValue.setValue(0);
    Animated.timing(animatedValue, {
      toValue: 1,
      duration: 3000,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start(() => {
      if (title === options.length - 1) {
        setTitle(0);
      } else {
        setTitle(title + 1);
      }
    });

    animatedMarginLeft.setValue(400);
    animatedMarginRight.setValue(0);
    Animated.sequence([
      Animated.parallel([
        Animated.timing(animatedMarginLeft, {
          toValue: 0,
          duration: 800,
          easing: Easing.linear,
          useNativeDriver: false,
        }),
        Animated.timing(animatedOpacity2, {
          toValue: 1,
          duration: 800,
          easing: Easing.linear,
          useNativeDriver: false,
        }),
      ]),
      Animated.parallel([
        Animated.timing(animatedMarginRight, {
          delay: 1400,
          toValue: 400,
          duration: 800,
          easing: Easing.linear,
          useNativeDriver: false,
        }),
        Animated.timing(animatedOpacity2, {
          delay: 1400,
          toValue: 0,
          duration: 800,
          easing: Easing.linear,
          useNativeDriver: false,
        }),
      ]),
    ]).start();
  }, [
    animatedMarginLeft,
    animatedOpacity2,
    animatedMarginRight,
    options.length,
    title,
    animatedValue,
  ]);

  const animateOpacity = useCallback(() => {
    Animated.timing(animatedOpacity, {
      toValue: 1,
      duration: 2000,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start();
  }, [animatedOpacity]);

  return (
    <Modal animationType="fade" statusBarTranslucent={false} transparent={true} visible={true}>
      <View style={styles.darkTransparent}>
        <View style={styles.circle}>
          <Icon style={styles.icon} name={icon} />
        </View>
        <Animated.View
          style={[
            styles.square,
            {
              opacity: animatedOpacity,
            },
          ]}
        >
          <Animated.Text
            style={[
              {
                opacity,
              },
              styles.textStyle2,
            ]}
          >
            {options[title]?.title}
          </Animated.Text>
          <View style={styles.textContainer}>
            <Animated.Text
              style={[
                styles.textStyle,
                {
                  left: animatedMarginLeft,
                  right: animatedMarginRight,
                  opacity: animatedOpacity2,
                },
              ]}
            >
              {options[title]?.desc}
            </Animated.Text>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const circleSize = scale(100);
const styles = StyleSheet.create({
  darkTransparent: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,.4)',
    ...CommonProperties.centerAlignment,
  },
  square: {
    backgroundColor: 'white',
    height: scale(200),
    width: '90%',
    maxWidth: 500,
    ...CommonProperties.centerAlignment,
    ...CommonProperties.borderRadius3,
  },
  circle: {
    width: circleSize,
    height: circleSize,
    borderRadius: circleSize / 2,
    marginBottom: -(circleSize / 2),
    backgroundColor: 'white',
    zIndex: 2,
    ...CommonProperties.centerAlignment,
    ...CommonProperties.shadow,
  },
  textStyle2: {
    color: Color.primary,
    textAlign: 'center',
    fontSize: scale(defaultFontSize.h1 + 5),
    marginTop: -scale(24),
    ...CommonProperties.fontFamily,
  },
  textContainer: {
    width: 800,
  },
  textStyle: {
    textAlign: 'center',
    position: 'absolute',
    color: Color.text.medium,
    ...CommonProperties.h2,
    ...CommonProperties.fontFamily,
    ...CommonProperties.mt3,
  },
  icon: {
    fontSize: scale(60),
    color: Color.primary,
  },
});

export default Interstitial;
